/**
 * TagsOrderConfirmationPage
 *
 * Post-checkout interstitial. The order-confirmation screen's "View order" button
 * lands here with the reconciliation id. At this point the charge has succeeded
 * but the Shopify order id is assigned asynchronously by the
 * payment_intent.succeeded webhook, so we poll the resolve endpoint and redirect
 * to the specific order (TAGS_ORDER) the moment it's ready — usually within ~1-2s.
 */
import React, { useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import { CircleAlertIcon } from 'lucide-react'

import { Stack, Typography } from '@mui/material'
import { Button } from '@repo/ui'
import { EmptyState, Loader } from 'components'

import { ROUTES } from 'routes'
import { useTagOrderResolution } from 'hooks/api/useTagOrders'

export const TagsOrderConfirmationPage = () => {
  const { reconciliationId } = useParams<{ reconciliationId: string }>()
  const navigate = useNavigate()
  const { data, isError } = useTagOrderResolution(reconciliationId)

  // Redirect to the specific order as soon as the webhook has assigned its id.
  useEffect(() => {
    if (data?.status === 'ready' && data.orderId) {
      navigate(ROUTES.TAGS_ORDER(data.orderId), { replace: true })
    }
  }, [data, navigate])

  // The charge succeeded but completion failed or the reconciliation is missing.
  // Reassure the user their payment went through and point them at their orders.
  if (isError || data?.status === 'failed') {
    return (
      <EmptyState
        icon={<CircleAlertIcon strokeWidth={1} size={32} />}
        title="Your order is still being finalized"
        text="Your payment went through. Your order will appear in your orders shortly."
        buttons={
          <Button variant="primary" onClick={() => navigate(ROUTES.TAGS_ORDERS)}>
            Go to orders
          </Button>
        }
      />
    )
  }

  // Pending / loading — waiting on the webhook to complete the order.
  return (
    <Stack
      alignItems="center"
      justifyContent="center"
      spacing={4}
      sx={{ height: '100%', minHeight: '60vh' }}
    >
      <Loader />
      <Typography variant="body2" color="text.secondary">
        Finalizing your order…
      </Typography>
    </Stack>
  )
}
